// 網站地圖的資料層（純函式；瀏覽器與 node 測試共用同一份）。
//
// 唯一來源＝src/data/route-manifest.json（scripts/build-route-manifest.cjs 產出的全站公開路由）。
// 這裡不讀任何其他資料、不加任何清單外的頁：清單裡有幾條路由，地圖上就有幾個網址。
//
// 一頁一個節點：同一頁的中英兩個網址（/work/x/ 與 /zh/work/x/）算一個節點，兩個網址都掛在 node.urls。
// 分群：先看網址階層（第一段），再看內容類型（GROUPS）。一頁只屬一群；不屬任何群的頁直接掛在網址上一層。
// 守恆：checkConservation() 驗「每條路由恰好落在一個節點」，scripts/test-sitemap-page.mjs 會跑。
import { KIND_LABEL, KIND_ITEM } from '../search/kinds.mjs'

export const pageKey = path => (path === '/zh/' ? '/' : path.replace(/^\/zh\//, '/'))
const parentKey = key => (key === '/' ? null : key.replace(/[^/]+\/$/, '') || '/')
const cleanTitle = title => String(title || '').split(' | ')[0].split('｜')[0].trim()

// 頁面元件 → 內容類型（顏色與圖示用；類型名沿用全站搜尋的 src/search/kinds.mjs）
const COMPONENT_KIND = {
  pageIndex: 'page', pageAbout: 'page', pageCV: 'page', pageContact: 'page', pageQuotes: 'page', pageLifetree: 'page',
  pageMuse: 'page', pageSearch: 'page', pageSitemap: 'page', pageResearch: 'page', pageExperiment: 'page',
  // 報導只有一頁索引頁（報導本身不是站內頁），併進「頁面」；音樂總頁也是「頁面」，2026-10-04 起每首歌有自己的頁（song 群）
  pageMusic: 'page', pagePress: 'page', pageThinking: 'essay', pageDialogues: 'dialogue', pageLineage: 'sketch',
  pageLabHome: 'lab', pageLab: 'lab', pageNft: 'edition', pageWorksShowcase: 'work', pageWorks: 'work',
  pageThesis: 'work', pageLexus2023: 'work', pageWorkDetail: 'work',
  pageExhibitionDetail: 'exhibition', pageDialogueDetail: 'dialogue', pageWritingDetail: 'essay',
  pageTdDetail: 'td', pageTdIndex: 'page', pageSketchDetail: 'sketch', pageLegacyProject: 'project', pageMusicTrack: 'song',
}

// 群組（順序＝圖例與清單的順序）。parent＝這一群掛在哪一頁底下：
//   rel 'contains'＝網址階層（/work/ 包含 /work/x/）；
//   rel 'lists'＝網址上一層不是一頁（/sketch/、/writing/、/exhibitions/、/project/ 都不是頁；/td/ 自 2026-10-18 是頁），改掛在實際列出它們的索引頁；
//   also＝另外也列出這一群的索引頁（2026-10-03 對正式站預渲染 HTML 實數過連結：
//         /work/archive/ 列作品、展覽、TD；/thinking/ 列對話錄）。
// byYear＝成員太多（草圖 783 頁），先分年份再展開。
export const GROUPS = [
  { id: 'work', kind: 'work', label: { zh: '作品', en: 'Works' }, match: k => /^\/work\/[^/]+\/$/.test(k) && k !== '/work/archive/', parent: '/work/', rel: 'contains', also: ['/work/archive/'] },
  { id: 'exhibition', kind: 'exhibition', label: { zh: '展覽與演出', en: 'Exhibitions and performances' }, match: k => /^\/exhibitions?\/[^/]+\/$/.test(k), parent: '/work/', rel: 'lists', also: ['/work/archive/'] },
  { id: 'dialogue', kind: 'dialogue', label: KIND_LABEL_PAIR('dialogue'), match: k => /^\/dialogues\/[^/]+\/$/.test(k), parent: '/dialogues/', rel: 'contains', also: ['/thinking/'] },
  { id: 'essay', kind: 'essay', label: KIND_LABEL_PAIR('essay'), match: k => /^\/writing\/[^/]+\/$/.test(k), parent: '/thinking/', rel: 'lists' },
  { id: 'edition', kind: 'edition', label: KIND_LABEL_PAIR('edition'), match: k => /^\/nft\/[^/]+\/$/.test(k), parent: '/nft/', rel: 'contains' },
  { id: 'strata', kind: 'lab', label: { zh: '地層：逐年', en: 'Strata by year' }, match: k => /^\/strata\/[^/]+\/$/.test(k), parent: '/strata/', rel: 'contains' },
  { id: 'td', kind: 'td', label: { zh: KIND_ITEM.zh.td, en: 'TouchDesigner dailies' }, match: k => /^\/td\/[^/]+\/$/.test(k), parent: '/td/', rel: 'contains', also: ['/work/archive/', '/lineage/'] },
  { id: 'sketch', kind: 'sketch', label: KIND_LABEL_PAIR('sketch'), match: k => /^\/sketch\/[^/]+\/$/.test(k), parent: '/lineage/', rel: 'lists', byYear: true },
  { id: 'song', kind: 'song', label: { zh: '歌曲', en: 'Songs' }, match: k => /^\/music\/[^/]+\/$/.test(k), parent: '/music/', rel: 'contains' },
  { id: 'project', kind: 'project', label: KIND_LABEL_PAIR('project'), match: k => /^\/project\/[^/]+\/$/.test(k), parent: '/work/archive/', rel: 'lists' },
]
function KIND_LABEL_PAIR(kind) { return { zh: KIND_LABEL.zh[kind], en: KIND_LABEL.en[kind] } }

// 圖例與清單用的類型（顏色＝類型；同一套 --k-* 色也用在全站的類型標籤 .tag.k-*）
export const KIND_ORDER = ['work', 'exhibition', 'dialogue', 'essay', 'edition', 'lab', 'td', 'sketch', 'project', 'song', 'press', 'page']
export const KIND_COLOR = {
  work: 'var(--k-work)', exhibition: 'var(--k-exhibition)', edition: 'var(--k-exhibition)', dialogue: 'var(--k-dialogue)',
  essay: 'var(--k-essay)', press: 'var(--k-press)', song: 'var(--k-music)', td: 'var(--k-td)', sketch: 'var(--k-sketch)',
  lab: 'var(--k-lab)', project: 'var(--k-archive)', page: 'var(--k-archive)',
}
export const kindName = (kind, locale) => (kind === 'lab'
  ? (locale === 'zh' ? '地層與實驗室' : 'Strata and Lab')
  : (KIND_LABEL[locale] || KIND_LABEL.en)[kind] || kind)

const yearOf = entry => String(entry.schema?.dateCreated || entry.lastmod || '').slice(0, 4)

/** route-manifest → { pages, groups, edges, byId, stats } */
export function buildSitemapModel(manifest) {
  if (!Array.isArray(manifest) || !manifest.length) throw new Error('sitemap: empty route manifest')
  const byKey = new Map()
  for (const entry of manifest) {
    const lang = entry.meta?.locale
    if (lang !== 'zh' && lang !== 'en') throw new Error('sitemap: route without locale ' + entry.path)
    const key = pageKey(entry.path)
    let page = byKey.get(key)
    if (!page) {
      page = { id: key, type: 'page', key, urls: {}, title: {}, kind: COMPONENT_KIND[entry.component] || 'page', component: entry.component, lastmod: '', year: '', noindex: true, group: null, subgroup: null, parent: null, layer: 0 }
      byKey.set(key, page)
    }
    if (page.urls[lang]) throw new Error(`sitemap: two ${lang} routes for one page ${key}`)
    page.urls[lang] = entry.path
    page.title[lang] = key === '/' ? (lang === 'zh' ? '首頁' : 'Home') : cleanTitle(entry.meta.title)
    if (entry.lastmod && entry.lastmod > page.lastmod) page.lastmod = entry.lastmod
    page.year ||= yearOf(entry)
    if (!entry.meta.noindex) page.noindex = false
  }
  const pages = [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key))

  // 分群
  const groups = GROUPS.map(g => ({ ...g, id: 'g:' + g.id, gid: g.id, type: 'group', members: [], subgroups: null, layer: 2 }))
  const groupOf = new Map(groups.map(g => [g.gid, g]))
  for (const page of pages) {
    const g = groups.find(gr => gr.match(page.key))
    // 成員的類型跟著群走（/exhibition/the-last-input/ 用作品頁元件，但它在「展覽與演出」這一群）
    if (g) { page.group = g.id; page.kind = g.kind; g.members.push(page.id) }
  }
  for (const g of groups) {
    if (!byKey.has(g.parent)) throw new Error(`sitemap: group ${g.gid} hangs on ${g.parent}, which is not a public page`)
    for (const p of g.also || []) if (!byKey.has(p)) throw new Error(`sitemap: group ${g.gid} also listed by missing page ${p}`)
    g.count = g.members.length
    if (g.byYear) {
      const years = new Map()
      for (const id of g.members) {
        const page = byKey.get(id), y = page.year || '—'
        if (!years.has(y)) years.set(y, { id: `${g.id}:${y}`, type: 'group', gid: g.gid, year: y, kind: g.kind, label: { zh: y, en: y }, members: [], layer: 3, parentGroup: g.id })
        years.get(y).members.push(id)
        page.subgroup = `${g.id}:${y}`
      }
      g.subgroups = [...years.values()].sort((a, b) => a.year.localeCompare(b.year))
      for (const s of g.subgroups) s.count = s.members.length
    }
  }
  const usedGroups = groups.filter(g => g.count > 0)

  // 網址階層：不在群裡的頁，掛在最近的「是一頁」的上層網址（/about/quotes/ → /about/；/exhibition/2023/ 上一層不是頁 → 首頁）
  for (const page of pages) {
    if (page.key === '/') { page.layer = 0; continue }
    if (page.group) continue
    let up = parentKey(page.key)
    while (up && !byKey.has(up)) up = parentKey(up)
    page.parent = up || '/'
    page.layer = page.parent === '/' ? 1 : 2
  }
  // 欄（layer）＝離首頁幾步：群組在它所屬索引頁的下一欄，年份子群再下一欄，成員在最後一欄
  for (const g of usedGroups) {
    g.layer = byKey.get(g.parent).layer + 1
    for (const s of g.subgroups || []) s.layer = g.layer + 1
    for (const id of g.members) byKey.get(id).layer = g.layer + (g.subgroups ? 2 : 1)
  }

  // 關係：contains（網址階層、群→成員）／lists（索引頁收錄一群）／also（另一個索引頁也收錄）
  const edges = []
  for (const page of pages) if (page.parent) edges.push({ source: page.parent, target: page.id, type: 'contains' })
  for (const g of usedGroups) {
    edges.push({ source: g.parent, target: g.id, type: g.rel })
    for (const p of g.also || []) edges.push({ source: p, target: g.id, type: 'also' })
    if (g.subgroups) {
      for (const s of g.subgroups) {
        edges.push({ source: g.id, target: s.id, type: 'contains' })
        for (const id of s.members) edges.push({ source: s.id, target: id, type: 'contains', member: true })
      }
    } else for (const id of g.members) edges.push({ source: g.id, target: id, type: 'contains', member: true })
  }
  const byId = new Map(pages.map(p => [p.id, p]))
  for (const g of usedGroups) { byId.set(g.id, g); for (const s of g.subgroups || []) byId.set(s.id, s) }
  const hasChildren = new Set(edges.map(e => e.source))
  for (const page of pages) page.hub = hasChildren.has(page.id)

  const routes = manifest.length
  const stats = {
    routes, pages: pages.length,
    en: manifest.filter(r => r.meta.locale === 'en').length, zh: manifest.filter(r => r.meta.locale === 'zh').length,
    bilingual: pages.filter(p => p.urls.en && p.urls.zh).length,
    single: pages.filter(p => !(p.urls.en && p.urls.zh)).length,
    noindexRoutes: manifest.filter(r => r.meta.noindex).length,
    noindexPages: pages.filter(p => p.noindex).length,
    groups: usedGroups.length,
    grouped: usedGroups.reduce((n, g) => n + g.count, 0),
    loose: pages.filter(p => !p.group).length,
    latest: pages.reduce((m, p) => (p.lastmod > m ? p.lastmod : m), ''),
  }
  return { pages, groups: usedGroups, edges, byId, stats }
}

/**
 * 圖上現在看得到的節點與連線。expanded＝展開中的群組 id（'g:sketch'、'g:sketch:2020'…）。
 * 收合的群組是一顆泡泡；展開後泡泡留著當這群的樞紐，成員掛在下一層。
 */
export function visibleGraph(model, expanded = new Set()) {
  const nodes = [], ids = new Set()
  const push = n => { if (!ids.has(n.id)) { ids.add(n.id); nodes.push(n) } }
  for (const p of model.pages) if (!p.group) push(p)
  for (const g of model.groups) {
    push(g)
    if (!expanded.has(g.id)) continue
    if (g.subgroups) {
      for (const s of g.subgroups) {
        push(s)
        if (expanded.has(s.id)) for (const id of s.members) push(model.byId.get(id))
      }
    } else for (const id of g.members) push(model.byId.get(id))
  }
  const edges = model.edges.filter(e => ids.has(e.source) && ids.has(e.target))
  return { nodes, edges }
}

/** 全部展開時的群組 id（含年份子群） */
export const allGroupIds = model => model.groups.flatMap(g => [g.id, ...(g.subgroups || []).map(s => s.id)])

/** 這一頁在目前語系要連去哪個網址：同語系優先，沒有就給另一種語言 */
export const urlFor = (page, locale) => page.urls[locale] || page.urls[locale === 'zh' ? 'en' : 'zh']
export const titleFor = (node, locale) => node.type === 'group'
  ? node.label[locale] || node.label.en
  : node.title[locale] || node.title[locale === 'zh' ? 'en' : 'zh'] || node.key

/** 搜尋：標題（中英）與網址都比對；回傳符合的頁 id */
export function searchPages(model, query) {
  const q = String(query || '').normalize('NFKC').toLowerCase().trim()
  if (!q) return null
  const terms = q.split(/\s+/).filter(Boolean)
  const hits = new Set()
  for (const p of model.pages) {
    const hay = [p.title.zh, p.title.en, p.key, p.year].filter(Boolean).join(' ').normalize('NFKC').toLowerCase()
    if (terms.every(t => hay.includes(t))) hits.add(p.id)
  }
  return hits
}

/**
 * 守恆檢查：route-manifest 的每一條路由恰好落在一個節點，不多不少；每個節點恰好在樹上一個位置。
 * 回傳錯誤字串陣列（空＝守恆）。
 */
export function checkConservation(manifest, model) {
  const errors = []
  const want = new Set(manifest.map(r => r.path))
  if (want.size !== manifest.length) errors.push('manifest has duplicate paths')
  const seen = new Map()
  for (const p of model.pages) for (const [lang, path] of Object.entries(p.urls)) {
    if (seen.has(path)) errors.push(`${path} 出現在兩個節點（${seen.get(path)}、${p.id}）`)
    seen.set(path, p.id)
    if (pageKey(path) !== p.key) errors.push(`${path} 掛錯節點 ${p.key}`)
    if ((lang === 'zh') !== path.startsWith('/zh/')) errors.push(`${path} 語言標錯（${lang}）`)
  }
  for (const path of want) if (!seen.has(path)) errors.push(`漏掉 ${path}`)
  for (const path of seen.keys()) if (!want.has(path)) errors.push(`多出清單外的 ${path}`)
  // 樹上位置：首頁是根；其餘每頁「在一群裡」或「掛在上層頁」，恰好其一
  const placed = model.pages.filter(p => p.key === '/' || (p.group ? !p.parent : !!p.parent)).length
  const memberCount = new Map()
  for (const g of model.groups) for (const id of g.members) memberCount.set(id, (memberCount.get(id) || 0) + 1)
  for (const p of model.pages) if (p.group && memberCount.get(p.id) !== 1) errors.push(`${p.id} 在群組裡出現 ${memberCount.get(p.id) || 0} 次`)
  const inGroups = [...memberCount.values()].reduce((a, b) => a + b, 0)
  if (inGroups !== model.pages.filter(p => p.group).length) errors.push(`群組成員 ${inGroups} ≠ 有群的頁 ${model.pages.filter(p => p.group).length}`)
  for (const g of model.groups) if (g.subgroups) {
    const sub = g.subgroups.reduce((n, s) => n + s.members.length, 0)
    if (sub !== g.members.length) errors.push(`${g.id} 年份子群 ${sub} ≠ 成員 ${g.members.length}`)
  }
  if (placed !== model.pages.length) errors.push(`樹上位置 ${placed} ≠ 節點 ${model.pages.length}`)
  const full = visibleGraph(model, new Set(allGroupIds(model))).nodes.filter(n => n.type === 'page').length
  if (full !== model.pages.length) errors.push(`全部展開後的頁節點 ${full} ≠ 節點 ${model.pages.length}`)
  return errors
}
